import {t} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf, RendererOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {BoxedInput} from '../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {BoxedInputElement} from '../../../libs/shared/ui/src/components/boxedInputs/boxedInputElement';
import {BoxedInputIcon} from '../../../libs/shared/ui/src/components/boxedInputs/boxedInputIcon';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ChromelessButton} from '../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {InputTypesEnum} from '../../../libs/shared/ui/src/components/inputs/input';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {center} from '../../../libs/shared/ui/src/styles/mixins';
import {useLocalize} from '../../../srcCommon/internationalization/internationalizationContext';
import {settingsHorizontalSpacing} from '../settingsCommonConstants';

/*
 * Props.
 */

interface SettingsContentHeaderProps {
  renderButtons?: RendererOf<React.ComponentProps<typeof Button>>;
  onSearchInput: HandlerOf<string>;
  searchInputValue: string;
  onExitClick: Handler;
  className?: string;
  placeholder?: string;
}

/*
 * Style.
 */

const StyledDiv = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-left: ${settingsHorizontalSpacing}px;
`;

const StyledSearchAndFiltersDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  flex-grow: 1;
  flex-shrink: 1;
  max-width: 296px;
  min-width: 130px;
  margin-right: 30px;

  > div {
    width: 100%;
  }
`;

const StyledButtonsDiv = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  grid-column-gap: 10px;
`;

const StyledExitButton = styled(ChromelessButton)`
  padding-right: 8px;
  margin-left: -24px;
  flex-shrink: 0;
  ${center()};
  color: ${(p) => p.theme.greys.shade60};

  &:hover {
    color: ${(p) => p.theme.greys.shade70};
  }
`;

/*
 * Component.
 */

export const SettingsContentHeader: FC<SettingsContentHeaderProps> = (props) => {
  const {searchInputValue, placeholder, className, onExitClick, onSearchInput, renderButtons} = props;

  const localize = useLocalize();

  return (
    <StyledDiv className={className}>
      <StyledSearchAndFiltersDiv>
        <BoxedInput size={VisualSizesEnum.MEDIUM} noMargin>
          {(childProps) => (
            <>
              <BoxedInputIcon {...childProps} iconName="searchSmall" />
              <BoxedInputElement
                {...childProps}
                type={InputTypesEnum.TEXT}
                value={searchInputValue}
                onChange={onSearchInput}
                style={{
                  width: '100%',
                  marginLeft: '-2px',
                }}
                placeholder={placeholder ?? localize(t('settings-header-search-placeholder')`Search`)}
              />
              {searchInputValue && (
                <StyledExitButton onClick={onExitClick}>
                  <Icon name="exitFilledSmall" />
                </StyledExitButton>
              )}
            </>
          )}
        </BoxedInput>
      </StyledSearchAndFiltersDiv>

      {renderButtons && (
        <StyledButtonsDiv>
          {renderButtons({
            size: VisualSizesEnum.MEDIUM,
          })}
        </StyledButtonsDiv>
      )}
    </StyledDiv>
  );
};

export const SettingsContentHeaderWithoutSearch: FC<
  Omit<SettingsContentHeaderProps, 'onExitClick' | 'onSearchInput' | 'searchInputValue' | 'placeholder'>
> = (props) => {
  const {className, renderButtons} = props;

  return (
    <StyledDiv className={className}>
      <StyledSearchAndFiltersDiv />

      {renderButtons && (
        <StyledButtonsDiv>
          {renderButtons({
            size: VisualSizesEnum.MEDIUM,
          })}
        </StyledButtonsDiv>
      )}
    </StyledDiv>
  );
};
